import { useRouter } from "next/router";
import useSWR from "swr";

import { useCallback, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import truncate from "lodash/truncate";
import { twMerge } from "tailwind-merge";

import { faClone } from "@fortawesome/free-regular-svg-icons";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import ShareButton from "components/ShareButton";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { convertCamelcaseObject } from "utils/common";
import { emberAppBaseUrl } from "utils/envVariables";

const filingSummarizationsUrl = (id: string | number) => {
  const url = new ApiEndpoint(`/api/v2/exchange-announcements/${id}`);
  url.searchParams.append("include", "summarizations");

  return url.href;
};

type SummaryData = {
  documentType?: string;
  impactScore?: number;
  sentiment?: string;
  bylineShort?: string;
  executiveSummary?: string[];
  keySections?: { sectionTitle: string; bulletSummary: string[] }[];
  biggestSurprises?: string[];
  conclusion?: string[];
  sourceUrl?: string;
};

const buildSummaryText = (
  summary: SummaryData | null,
  title?: string,
  link?: string
): string => {
  if (!summary) return title ?? "";

  const lines: string[] = [];
  const addSection = (heading: string, points?: string[]) => {
    if (!points?.length) return;
    lines.push(heading);
    points.forEach((point) => lines.push(`- ${point}`));
    lines.push("");
  };

  if (title) {
    lines.push(title);
    lines.push("");
  }
  if (summary.bylineShort) {
    lines.push(summary.bylineShort);
    lines.push("");
  }

  const meta: string[] = [];
  if (summary.impactScore != null)
    meta.push(`Impact: ${summary.impactScore}/5`);
  if (summary.sentiment) meta.push(`Sentiment: ${summary.sentiment}`);
  if (meta.length) {
    lines.push(meta.join("  |  "));
    lines.push("");
  }

  addSection("Executive Summary", summary.executiveSummary);

  summary.keySections?.forEach((item) => {
    const section = convertCamelcaseObject(item);
    addSection(section.sectionTitle, section.bulletSummary);
  });

  addSection("Biggest Surprises", summary.biggestSurprises);
  addSection("Conclusion", summary.conclusion);

  if (link) {
    lines.push(link);
  }

  return lines.join("\n").trim();
};

const parseSummaryContent = (content: any): SummaryData | null => {
  if (!content) return null;
  try {
    const raw = Array.isArray(content)
      ? content[0]?.content ?? content[0]
      : content;
    if (!raw) return null;
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};

const SummaryModalContent = ({
  content,
  item,
  onClose,
}: {
  content: any;
  item: any;
  onClose: () => any;
}) => {
  const router = useRouter();
  const summary = parseSummaryContent(content);
  const [copied, setCopied] = useState(false);

  const sourceUrl = useMemo(() => {
    if (summary?.sourceUrl) return summary.sourceUrl;
    if (Array.isArray(content)) return content[0]?.sourceUrl;
    return content?.sourceUrl;
  }, [content, summary?.sourceUrl]);

  const localSummarizationId = useMemo(() => {
    if (Array.isArray(content) && content[0]?.id != null) return content[0].id;
    if (content?.id != null) return content.id;

    const matched = (item?.summarizations ?? []).find(
      (summarization: any) => summarization.sourceUrl === sourceUrl
    );

    return matched?.id;
  }, [content, item?.summarizations, sourceUrl]);

  const { data: filing } = useSWR(
    localSummarizationId == null &&
      item?.type === "exchange-announcements" &&
      item?.id
      ? filingSummarizationsUrl(item.id)
      : null,
    baseFetcher
  );

  const summarizationId = useMemo(() => {
    if (localSummarizationId != null) return localSummarizationId;

    const matched = (filing?.summarizations ?? []).find(
      (summarization: any) => summarization.sourceUrl === sourceUrl
    );

    return matched?.id;
  }, [localSummarizationId, filing?.summarizations, sourceUrl]);

  const shareUrl = useMemo(() => {
    const base = `${emberAppBaseUrl}/entities/${router?.query?.slug}/announcements`;
    const summarizationParam =
      summarizationId != null ? `&summarization=${summarizationId}` : "";

    if (item?.type === "corporate-events") {
      return `${base}?tab=events&item=${item?.id}${summarizationParam}`;
    }
    if (item?.type === "corporate-presentations") {
      return `${base}?tab=presentations&item=${item?.id}${summarizationParam}`;
    }
    if (item?.type === "entity-earnings-alerts") {
      return `${base}?tab=earnings-alerts&item=${item?.id}${summarizationParam}`;
    }
    if (item?.type === "exchange-announcements") {
      return `${base}?item=${item?.id}${summarizationParam}`;
    }
    if (item?.type === "entity-news") {
      return item?.url.startsWith("https://")
        ? item?.url
        : `https://${item?.url}`;
    }
    return "";
  }, [item?.id, item?.type, item?.url, router?.query?.slug, summarizationId]);

  const type = useMemo(() => {
    if (item?.type === "corporate-events") return "Event";
    if (item?.type === "corporate-presentations") return "Presentation";
    if (item?.type === "exchange-announcements") return "Filing";
    if (item?.type === "entity-earnings-alerts") return "Earnings";
    if (item?.type === "entity-news") return "News";
    return "";
  }, [item?.type]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        buildSummaryText(summary, item?.title, shareUrl)
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // no-op
    }
  };

  const shareBtnOptions = useCallback(
    (link?: string) => {
      if (!item) return {};
      const shareLink = link || shareUrl;
      const clipboardText = `${item?.entity?.shortName} | ${
        item?.title
      }: ${encodeURIComponent(shareLink)}`;
      const whatsappText = `Check this out ${clipboardText}`;
      const twitterText = truncate(item?.title, { length: 100 });

      return {
        email: {
          link: `mailto:?subject=✪ Smartkarma | ${type}: | ${item?.title}&body=${whatsappText}`,
        },
        clipboard: {
          message: shareLink,
        },
        privateMessage: {
          message: `Check this out <a href="${shareLink}">${item?.entity?.shortName} | ${item?.title}</a>`,
        },
        whatsapp: {
          link: `https://web.whatsapp.com/send?text=${whatsappText}`,
        },
        twitter: {
          link: `https://twitter.com/share?url=${shareLink}&text=${twitterText}&via=smartkarma`,
        },
        linkedIn: {
          link: `https://www.linkedin.com/shareArticle?mini=true&url=${shareLink}&source=Smartkarma`,
        },
      };
    },
    [item, shareUrl, type]
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <div className="flex-shrink-0 px-[24px] pt-[24px] pb-[16px] border-0 border-b border-solid border-[#E8E8E8]">
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between mb-[10px] md:mb-0">
          <div className="flex flex-col md:flex-row md:items-center gap-0 md:gap-[10px]">
            <div className="flex items-center gap-[5px] md:gap-[10px] mb-[5px] md:mb-0">
              <span className="h-1.5 w-1.5 rounded-full bg-smart-teal" />
              <span className="text-[12px] font-medium uppercase tracking-[0.04em] text-[#808080]">
                {summary?.documentType ?? item?.entity?.shortName}
              </span>
            </div>
            <span className="text-inactive-gray hidden md:block">|</span>
            {summary?.impactScore != null && (
              <div className="flex items-center gap-[6px] border border-gray-200 rounded-full md:py-[3px]">
                <span className="text-[12px] text-intelligent-silver">
                  Impact
                </span>
                <span className="text-[12px] font-semibold">
                  {summary.impactScore}
                </span>
                <div className="w-[40px] h-[4px] rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-400"
                    style={{ width: `${(summary.impactScore / 5) * 100}%` }}
                  />
                </div>
              </div>
            )}
            {summary?.sentiment && (
              <div className="flex items-center gap-[6px] border border-gray-200 rounded-full md:py-[3px]">
                <span className="text-[12px] text-intelligent-silver">
                  Sentiment
                </span>
                <span
                  className={twMerge(
                    "text-[12px] font-semibold",
                    summary.sentiment.toLowerCase() === "bullish" &&
                      "text-bullish-green",
                    summary.sentiment.toLowerCase() === "bearish" &&
                      "text-bearish-red"
                  )}
                >
                  {summary.sentiment}
                </span>
              </div>
            )}
          </div>

          <div className="absolute right-0 flex items-center gap-[15px]">
            <ShareButton
              shareKey={`summarization-${item?.type}-${item?.id}`}
              containerClassName="flex items-center text-intelligent-silver mr-[3px] justify-center rounded-full hover:text-smart-teal"
              activeClassName="!block"
              iconClassName="text-[16px]"
              options={shareBtnOptions()}
              columnView
              noBorder
              tooltipPlacement="bottom"
            />
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Open filing</Tooltip>}
            >
              <span className="inline-flex h-[40px] items-center">
                <a
                  href={summary?.sourceUrl || sourceUrl}
                  target="_blank"
                  className={twMerge(
                    "flex w-[24px] h-[24px] text-shady-gray hover:text-smart-teal !text-[16px] transition-colors",
                    "items-center justify-center rounded-full border-0 bg-transparent"
                  )}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
              </span>
            </OverlayTrigger>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>{copied ? "Copied" : "Copy to Clipboard"}</Tooltip>
              }
            >
              <span className="inline-flex h-[40px] items-center">
                <button
                  onClick={handleCopy}
                  className={twMerge(
                    "flex h-[28px] w-[28px] items-center justify-center rounded-full border-0 bg-transparent text-[32px] transition-colors",
                    copied
                      ? "text-bullish-green"
                      : "text-shady-gray hover:text-smart-teal"
                  )}
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faClone} />
                </button>
              </span>
            </OverlayTrigger>
            <button
              className={twMerge(
                "flex items-center justify-center border-0 bg-transparent text-[32px] px-0 text-shady-gray hover:text-active-black h-[40px]"
              )}
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <h2 className="text-[20px] font-bold mb-0 leading-snug">
          {item?.title}
        </h2>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-[24px] py-[16px]">
        {summary?.bylineShort && (
          <p className="text-[15px] font-medium mb-[15px]">
            {summary.bylineShort}
          </p>
        )}
        {!!summary?.executiveSummary?.length && (
          <div className="mb-[4px]">
            <div className="text-[11px] font-semibold text-smart-teal uppercase tracking-wider mb-[8px]">
              Executive Summary
            </div>
            <ul className="list-disc pl-[20px] space-y-[6px]">
              {summary.executiveSummary.map((point, i) => (
                <li key={i} className="text-[14px]">
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {summary?.keySections?.map((item, i) => {
          const section = convertCamelcaseObject(item);

          return (
            <div key={i}>
              <div className="border-0 border-t border-solid border-[#E8E8E8] my-[12px]" />
              <div className="font-semibold text-[14px] mb-[8px]">
                {section.sectionTitle}
              </div>
              <ul className="list-disc pl-[20px] space-y-[4px]">
                {section.bulletSummary?.map((bullet: any, j: any) => (
                  <li key={j} className="text-[14px]">
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {!!summary?.biggestSurprises?.length && (
          <div>
            <div className="border-0 border-t border-solid border-[#E8E8E8] my-[12px]" />
            <div className="font-semibold text-[14px] mb-[8px]">
              Biggest Surprises
            </div>
            <ul className="list-disc pl-[20px] space-y-[4px]">
              {summary.biggestSurprises.map((point, i) => (
                <li key={i} className="text-[14px]">
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!!summary?.conclusion?.length && (
          <div>
            <div className="border-0 border-t border-solid border-[#E8E8E8] my-[12px]" />
            <div className="font-semibold text-[14px] mb-[8px]">Conclusion</div>
            <ul className="list-disc pl-[20px] space-y-[4px]">
              {summary.conclusion.map((point, i) => (
                <li key={i} className="text-[14px]">
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-0 border-t border-solid border-[#E8E8E8] mt-[12px] pt-[12px]">
          <span className="text-[12px] text-intelligent-silver">
            Smartkarma AI
          </span>
        </div>
      </div>

      <div className="flex-shrink-0 border-0 border-t border-solid border-[#E8E8E8] bg-white px-[24px] py-[12px]">
        <p className="m-0 text-[12px] leading-5 text-[#808080]">
          <strong>Disclaimer:</strong> This content is AI-generated and
          displayed for general informational purposes only. Please verify
          independently before use.
        </p>
      </div>
    </div>
  );
};

export default SummaryModalContent;
